// The product is the unit; terms sit under it.
//
// Every figure says which of three things it is. Amazon's own number, our
// allocation of one, or absent — and the third is written out rather than
// drawn as a zero, because "none of this came from search" and "the report was
// never collected" are different answers about a product.
import React, { useState } from 'react';
import { FiAlertTriangle, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import s from '../SearchAttribution.module.css';
import { money, num, pct } from '../format';

function Figure({ label, value, basis, note }) {
  return (
    <div className={s.figure}>
      <span className={s.figureLabel}>{label}</span>
      <span className={s.figureValue}>{value}</span>
      {basis && <span className={s.basis}>{basis}</span>}
      {note && <span className={s.figureNote}>{note}</span>}
    </div>
  );
}

function Block({ p, threshold }) {
  const [open, setOpen] = useState(false);
  const depends = p.dependency || {};
  return (
    <section className={s.block}>
      <header className={s.blockHead}>
        <button className={s.expandButton} onClick={() => setOpen(!open)}
                aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <FiChevronDown /> : <FiChevronRight />}
        </button>
        <span className={s.blockAsin}>{p.asin}</span>
        {depends.flagged && (
          <span className={s.flag}>
            <FiAlertTriangle /> {pct(depends.share, 0)} on one term
          </span>
        )}
      </header>

      <div className={s.figures}>
        <Figure label="Revenue from search"
                value={p.search_revenue_reported ? money(p.search_revenue) : 'not collected'}
                basis={p.search_revenue_reported ? 'Amazon’s figure' : null}
                note={p.search_revenue_reported && !p.search_revenue
                  ? 'none of this product’s revenue came from search' : null} />
        <Figure label="Share of its revenue"
                value={p.share_of_revenue == null ? '—' : pct(p.share_of_revenue, 1)}
                note={p.revenue_error
                  ? `Amazon's two reports disagree — the catalogue report credits this `
                    + `product with search revenue and the traffic report reports `
                    + `${money(p.total_revenue)} in total. No share is shown rather than `
                    + `one above 100%.`
                  : p.total_revenue == null ? 'no Sales and Traffic figure for this window'
                  : !p.total_revenue ? 'this product earned nothing this window' : null} />
        <Figure label="Share of its sessions"
                value={p.share_of_sessions == null ? '—' : pct(p.share_of_sessions, 1)}
                note={p.sessions_note} />
        {/* The PRD asks for dependency two ways and they are different numbers,
            so both are here and each says its denominator. The flag fires on
            the first: the same clicks against all sessions run under 5% on
            every product, and a flag set there would never fire. */}
        <Figure label="Of its search clicks"
                value={depends.share == null ? '—' : pct(depends.share, 0)}
                note={depends.top_term
                  ? (depends.rankable
                      ? `“${depends.top_term}” · flag at ${pct(threshold, 0)}`
                      : `“${depends.top_term}” · too few search clicks to flag`)
                  : 'no search clicks this window'} />
        <Figure label="Of its total sessions"
                value={depends.share_of_all_sessions == null
                  ? '—' : pct(depends.share_of_all_sessions, 1)}
                note={depends.share_of_all_sessions == null
                  ? depends.share_of_all_sessions_basis
                  : 'what losing that term would cost in traffic overall'} />
      </div>

      {open && (
        <div className={s.blockBody}>
          {p.terms?.length ? (
            <>
              <table className={s.subTable}>
                <thead>
                  <tr>
                    <th>Term</th><th className={s.right}>Revenue</th>
                    <th className={s.right}>Purchases</th><th className={s.right}>Clicks</th>
                    <th className={s.right}>Price paid</th><th>Segment</th>
                  </tr>
                </thead>
                <tbody>
                  {p.terms.map((t) => (
                    <tr key={t.search_term}>
                      <td>{t.search_term}</td>
                      <td className={s.right}>
                        {money(t.revenue)}<span className={s.subFigure}>allocated</span>
                      </td>
                      <td className={s.right}>{num(t.purchases)}</td>
                      <td className={s.right}>{num(t.clicks)}</td>
                      <td className={s.right}>{money(t.median_price)}</td>
                      <td>{t.long_tail ? <span className={s.tagTail}>long tail</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className={s.hint}>
                {money(p.totals.allocated)} allocated across these terms,{' '}
                {money(p.totals.unallocated)} left unallocated.
                {p.totals.unpriced_terms > 0 && (
                  <> {num(p.totals.unpriced_terms)} terms had purchases but no median price, so
                    their revenue was not spread over the others.</>
                )}
                {p.totals.missing_tail_purchases > 0 && (
                  <> {num(p.totals.missing_tail_purchases)} search purchases sit on terms
                    Amazon truncated from this product&apos;s report; their revenue stays
                    unallocated rather than being moved onto the terms on hand.</>
                )}
              </p>
            </>
          ) : (
            <p className={s.hint}>
              No term on this product carried both a purchase and a price, so there is nothing
              to allocate its search revenue across.
            </p>
          )}

          <p className={s.hint}>
            Long tail: {num(p.long_tail.terms)} terms of {p.long_tail.word_threshold}+ words,{' '}
            {num(p.long_tail.terms_valued)} of them valued at {money(p.long_tail.revenue)}
            {p.long_tail.share_of_search_clicks != null && (
              <> · {pct(p.long_tail.share_of_search_clicks, 0)} of this product&apos;s search
                clicks</>
            )}. Margin absent — {p.long_tail.margin_note}.
          </p>
        </div>
      )}
    </section>
  );
}

export default function ProductBlocks({ data, loading }) {
  if (loading && !data) return <div className={s.empty}>Loading…</div>;
  if (!data?.products?.length) {
    return <div className={s.empty}>No products with search data for this window.</div>;
  }
  return (
    <>
      <p className={s.hint} style={{ marginBottom: 14 }}>
        {num(data.sources?.search_revenue)} products have a search revenue figure,{' '}
        {num(data.sources?.totals)} have Sales and Traffic totals to compare it against, and{' '}
        {num(data.sources?.terms)} have search terms to allocate it across. A product missing
        one of the three says so rather than showing a zero.
      </p>
      {data.products.map((p) => (
        <Block key={p.asin} p={p} threshold={data.dependency_threshold} />
      ))}
    </>
  );
}
